import React, { useState, useEffect, useRef } from "react";
import "./RecruitmentListFilter.css";
import config from "../../config";
import { LuChevronDown, LuCalendar } from "react-icons/lu";

export default function RecruitmentListFilter({
  open,
  onClose,
  onApply,
  onClear,
  clientOptions = [],
  statusOptions = [],
  jobRoleOptions = [],
  sortOptions = [],
  initialValues,
}) {
  const [form, setForm] = useState({
    client: "",
    status: "",
    jobRole: "",
    startDate: "",
    endDate: "",
    sortBy: "",
  });

  // Local option states (fallbacks if props not provided)
  const [clientItems, setClientItems] = useState([]);
  const [jobRoleItems, setJobRoleItems] = useState([]);
  const [jobRolesLoaded, setJobRolesLoaded] = useState(false);
  const jobRoleSelectRef = useRef(null);

  useEffect(() => {
    if (open && initialValues) {
      setForm((prev) => ({ ...prev, ...initialValues }));
    }
  }, [open, initialValues]);

  // Fetch client (id: 6) when filter opens, populate dropdown
  useEffect(() => {
    if (!open) return;
    const token = localStorage.getItem("Authorization") || "";
    const abort = new AbortController();

    const fetchClient = async () => {
      try {
        const orgId = localStorage.getItem("orgId") || "6"; // user showed orgKey=6 in their logs
        const url = `${config.API_BASE_URL}${config.CLIENT_LIST_ENDPOINT}${orgId}`;
        const res = await fetch(url, {
          method: "GET",
          headers: {
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
            "Content-Type": "application/json",
          },
          credentials: "include",
          signal: abort.signal,
        });
        if (!res.ok) throw new Error("Failed to fetch client");
        const data = await res.json();
        // data structure for clients often has 'body' or is a plain list
        const items = Array.isArray(data)
          ? data
          : Array.isArray(data.body)
          ? data.body
          : data
          ? [data]
          : [];
        const mapped = items.map((c) => ({
          value: c.name || c.clientName || `Client ${c.id || c.clientKey || c.id}`,
          label: c.name || c.clientName || `Client ${c.id || c.clientKey || c.id}`,
        }));
        setClientItems(mapped);
      } catch (e) {
        console.error("Fetch client error:", e);
        // Fallback or leave empty
        setClientItems([]);
      }
    };

    fetchClient();
    return () => abort.abort();
  }, [open]);

  // Fetch Job Roles when user focuses/clicks Job Role select (id: 6)
  const loadJobRoles = async () => {
    if (jobRolesLoaded) return;
    const token = localStorage.getItem("Authorization") || "";
    const orgId = localStorage.getItem("orgId") || "6";
    try {
      const url = `${config.API_BASE_URL}${config.VIEW_CLIENT_CONFIG_ENDPOINT}${orgId}?configType=JobRole`;
      const res = await fetch(url, {
        method: "GET",
        headers: {
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
          "Content-Type": "application/json",
        },
        credentials: "include",
      });
      if (!res.ok) throw new Error("Failed to fetch job roles");
      const data = await res.json();
      const list = Array.isArray(data) ? data : data?.data || data?.content || [];
      const mapped = list
        .filter((i) => i.isActive !== false)
        .map((r, idx) => ({
          value: r.values ?? r.name ?? r.value ?? idx,
          label: r.values ?? r.name ?? r.value ?? `Role ${idx + 1}`,
        }));
      setJobRoleItems(mapped);
      setJobRolesLoaded(true);
    } catch (e) {
      // Provide minimal fallback
      setJobRoleItems([
        { value: "Developer", label: "Developer" },
        { value: "Tester", label: "Tester" },
      ]);
      setJobRolesLoaded(true);
    }
  };

  // Ensure job roles are fetched as soon as filter opens
  useEffect(() => {
    if (open && !jobRolesLoaded) {
      loadJobRoles();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  if (!open) return null;

  const stop = (e) => e.stopPropagation();
  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((p) => ({ ...p, [name]: value }));
  };
  const handleClear = () => {
    setForm({ client: "", status: "", jobRole: "", startDate: "", endDate: "", sortBy: "" });
    onClear && onClear();
    onClose && onClose();
  };
  const handleApply = () => onApply && onApply(form);

  // Resolve options: prefer props, else local state/defaults
  const resolvedClientOptions = clientOptions?.length ? clientOptions : clientItems;
  const resolvedStatusOptions = (statusOptions?.length
    ? statusOptions
    : [
        { value: "Open", label: "Open" },
        { value: "Close", label: "Close" },
      ]).map((opt) => ({ value: opt.value ?? opt.label, label: opt.label ?? String(opt.value ?? "") }));
  const resolvedJobRoleOptions = (jobRoleOptions?.length ? jobRoleOptions : jobRoleItems).map((opt) => ({
    value: opt.value ?? opt.label,
    label: opt.label ?? String(opt.value ?? ""),
  }));
  const resolvedSortOptions = (sortOptions?.length
    ? sortOptions
    : [
        { value: "DATE_DESC", label: "Date: Latest" },
        { value: "DATE_ASC", label: "Date: Oldest" },
        { value: "NAME_ASC", label: "Name: A → Z" },
        { value: "NAME_DESC", label: "Name: Z → A" },
      ]).map((opt) => ({ value: opt.value ?? opt.label, label: opt.label ?? String(opt.value ?? "") }));

  return (
    <div className='rl-filter-overlay' onClick={onClose}>
      <div className='rl-filter-modal' onClick={stop}>
        <div className='rl-filter-header'>Filter</div>

        <div className='rl-filter-body'>
          <div className='filter-field'>
            <label className='rl-filter-label'>Client Name</label>
            <div className='select-wrapper'>
              <select
                name='client'
                className='rl-filter-select'
                value={form.client}
                onChange={handleChange}
              >
                <option value=''></option>
                {resolvedClientOptions.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
              <LuChevronDown className='select-icon' />
            </div>
          </div>

          <div className='filter-field'>
            <label className='rl-filter-label'>Status</label>
            <div className='select-wrapper'>
              <select
                name='status'
                className='rl-filter-select'
                value={form.status}
                onChange={handleChange}
              >
                <option value=''></option>
                {resolvedStatusOptions.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
              <LuChevronDown className='select-icon' />
            </div>
          </div>

          <div className='filter-field'>
            <label className='rl-filter-label'>Job Role</label>
            <div className='select-wrapper'>
              <select
                name='jobRole'
                className='rl-filter-select'
                value={form.jobRole}
                onChange={handleChange}
                onFocus={loadJobRoles}
                onClick={loadJobRoles}
                ref={jobRoleSelectRef}
              >
                <option value=''></option>
                {resolvedJobRoleOptions.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
              <LuChevronDown className='select-icon' />
            </div>
          </div>

          <div className='filter-field'>
            <label className='rl-filter-label' style={{ fontWeight: "600", marginBottom: "8px" }}>Interview Date</label>
            <div className='rl-grid-2'>
              <div className='date-field-wrapper'>
                <label className='date-sub-label'>Start Date</label>
                <div className='input-wrapper'>
                  <input
                    type='date'
                    name='startDate'
                    className='rl-filter-input'
                    value={form.startDate}
                    onChange={handleChange}
                  />
                  <LuCalendar className='input-icon' />
                </div>
              </div>
              <div className='date-field-wrapper'>
                <label className='date-sub-label'>End Date</label>
                <div className='input-wrapper'>
                  <input
                    type='date'
                    name='endDate'
                    className='rl-filter-input'
                    value={form.endDate}
                    onChange={handleChange}
                  />
                  <LuCalendar className='input-icon' />
                </div>
              </div>
            </div>
          </div>

          <div className='filter-field'>
            <label className='rl-filter-label'>Sort By</label>
            <div className='select-wrapper'>
              <select
                name='sortBy'
                className='rl-filter-select'
                value={form.sortBy}
                onChange={handleChange}
              >
                <option value=''></option>
                {resolvedSortOptions.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
              <LuChevronDown className='select-icon' />
            </div>
          </div>
        </div>

        <div className='rl-filter-actions'>
          <button className='rl-btn rl-btn-clear' onClick={handleClear}>
            Clear
          </button>
          <button className='rl-btn rl-btn-apply' onClick={handleApply}>
            Apply
          </button>
        </div>
      </div>
    </div>
  );
}
